
import { BlogPost } from "@/types/blog";

export const post: BlogPost = {
  slug: "rejection-rates-costs-and-regulatory-changes",
  title: "Rejection Rates, Costs and Regulatory Changes",
  excerpt: "An in-depth look at permit rejection rates across municipalities and their impact on construction and energy project costs.",
  content: `
    <h2>Permitting is challenging</h2>
    
    <p>
      Residential construction, development and energy projects often face significant challenges during the permitting process, 
      leading to permit rejections that can delay or derail projects. These rejections can occur for a variety of reasons, each 
      linked to different aspects of building codes, local regulations, or other concerns. Below we'll dive into where these 
      rejections occur, relevant statistics, and the associated cost impact for those involved. We'll also explore some of the 
      upcoming regulatory changes that will impact each of these markets, ensuring those involved can set themselves up for success 
      as these changes are adopted by states and local municipalities.
    </p>
    
    <h2>Municipal Code and Building Permit Rejection Rates</h2>
    
    <p>
      Code and requirement variability can drastically impact project timelines and outcomes. Codes vary from city to city, 
      and are generally based on how the city wants to be growing and in what ways. This local focus means every city's rejection 
      rates also vary. However, there is general understanding on the following rates:
    </p>
    
    <ul>
      <li>Small cities: Approximately 10-15% rejection rate</li>
      <li>Medium cities: 20-25% rejection rate</li>
      <li>Large cities: 30-40% rejection rate</li>
    </ul>
    
    <p>
      In a future article, We'll dive deeper into the major points of failure as well as specific codes (with municipal examples) 
      that commonly lead to these permit rejections.
    </p>
    
    <h2>How this impacts the bottom line</h2>
    
    <p>
      For energy projects, delays due to permit rejections can break a project's margins. These additional costs can include 
      re-submission fees, additional material warehousing costs, and higher professional labor costs (project engineers, 
      permit technicians, scheduling coordinators, etc) due to the longer project duration. For example, re-submission fees 
      and related costs can add $200 to $1,000 per project, depending on the jurisdiction and the complexity of the changes required.
    </p>
    
    <p>
      The skinny: in total, a rejected energy permit can cost $470 to $1750 per project. Extrapolating this over a 15-20% 
      national permit rejection rate, the costs become incredibly painful.
    </p>
    
    <p>
      Many solar installers operate on tight margins and rely on prompt payments from customers and financiers. These permit 
      delays can push back the completion of installations, which end up delaying critical payments. Energy companies that fail 
      to navigate the permitting process quickly may also lose customers to competitors, leading to lost revenue. For a typical 
      residential solar project costing around $20,000, losing even a few customers can have a significant impact on a company's bottom line.
    </p>
    
    <p>
      Coupling this with the current financial market and regulatory swings, these factors have led to a wave of bankruptcies 
      in the solar industry. In 2023, over 100 residential solar installers filed for bankruptcy.
    </p>
    
    <p>
      Residential construction and development projects face a multitude of their own challenges during the permitting process 
      that lead to increased costs. These can include holding costs (taxes, insurance and interest), staff and overhead, redesign 
      and re-submission fees, penalties for delayed completion, material inflation, as well as legal fees. For example, a 12-month 
      delay in 2021 led to over 8% increase in build price - applying that to the national average cost to build a single-family home 
      (ignoring land costs), this translates into $18,400 in additional costs over that time-frame.
    </p>
    
    <p>
      The skinny: Rejected permits can lead to upwards of 11% in additional costs that were not previously budgeted, translating 
      into between $3500 to $25,000 for a typical single-family build.
    </p>
    
    <p>
      Lost revenue opportunities are also very real. These can be felt by missed market timing as well as customer cancellation 
      of pre-sales or lease agreements. Moreover, the long-term financial viability of a developer can be significantly affected 
      by repeated permit rejections, as these can damage market reputation and make future projects more challenging to finance and execute.
    </p>
    
    <h2>Preparing for Regulatory and Municipal Code Changes</h2>
    
    <p>
      Below are a handful of the upcoming regulatory changes poised to reshape the residential construction and solar industries. 
      These changes include stricter energy efficiency standards and updates to building codes that could influence project costs 
      and timelines. Builders and solar installers need to stay informed and adapt their practices to comply with these new regulations 
      to avoid delays, increased costs, and potential business disruptions.
    </p>
    
    <h3>Massachusetts: 10th Edition Building Codes - January 1, 2025</h3>
    
    <p>
      The updated codes will include more robust energy requirements, and it's anticipated that many of the Stretch energy codes 
      will be incorporated into this new edition. From a 2020 FEMA study, it's said that homeowner savings could amount to 3.2 billion 
      dollars a year by 2040 with this new edition. Specifically for solar and energy storage (ESS) installers, the new code will 
      require smoke alarm and carbon monoxide protection for ESS systems. Specifically for residential home builders, there is an 
      increased focus on resilient design to address climate change impacts, including a higher level of freeboard to accommodate storm surge.
    </p>
    
    <h3>Denver, Colorado: New Green Roof Initiative - January 1, 2025</h3>
    
    <p>
      The existing Green Building Ordinance will expand in 2025 to include stricter requirements for green roofs on new residential 
      buildings. Developers will be required to include green roof systems, reflective roofing materials, or a combination of the 
      two in order to reduce the urban heat island effect and improve energy efficiency.
    </p>
    
    <p>
      Builders will need to incorporate green roof technology into their designs, which may increase upfront construction costs but 
      could lead to long-term energy savings and compliance with city sustainability goals. Additionally, these changes could affect 
      project timelines as green roof installations can be complex and require specialized expertise
    </p>
    
    <h3>California: 2025 Building Energy Efficiency Standards - January 1, 2026</h3>
    
    <p>
      These new guidelines will impose stricter energy requirements on newly constructed buildings, additions, and alterations. 
      These standards will include enhanced insulation, improved HVAC systems, and new requirements for renewable energy integration.
    </p>
    
    <p>
      Builders will need to incorporate more energy-efficient designs, which could increase upfront construction costs but reduce 
      long-term energy consumption. Solar installers might see increased demand for photovoltaic systems as part of compliance with 
      these new energy standards.
    </p>
    
    <h3>San Francisco, California: Concrete Building Safety Program - TBD</h3>
    
    <p>
      If legislation passes, this upcoming ordinance mandates that all older non-ductile concrete (brittle concrete structural elements) 
      structures be upgraded to account for seismic activity. Concrete lacking the proper reinforcement will crack and fail – which 
      poses a significant risk to life and property in the event of an earthquake.
    </p>
    
    <p>
      Builders completing building retrofits will will need to strengthen supporting structures by wrapping columns with rebar, 
      reinforcing existing rebar with additional ties, creating shear walls, or other solutions. This can cost builders upwards of 
      $50 to $100 per square foot, excluding engineering costs.
    </p>
    
    <h2>Learn More!</h2>
    
    <p>
      Up next, we'll discuss specific codes across the country that lead to higher permit rejections rates, and provide tangible ways 
      to lower the barriers to entry when your company is expanding into neighboring municipalities and geographies.
    </p>
  `,
  coverImage: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80", // Books piled in shelves
  date: "2024-10-14T12:00:00",
  readTime: 8,
  categories: ["AI", "Construction"],
  author: {
    name: "Richie Rasor",
    title: "Industry Expert",
    avatar: "/lovable-uploads/26c946ec-4296-4ac9-9351-78321594ea25.png"
  }
};
